import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useColorMode } from '../../themes/AppThemeProvider';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setGlobalSearch, toggleSidebar } from '../../redux/uiSlice';
import ProjectSprintSwitcher from './ProjectSprintSwitcher';

interface Props {
  drawerWidth: number;
}

export default function TopBar({ drawerWidth }: Props) {
  const { user, logout } = useAuth();
  const { mode, toggle } = useColorMode();
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector((s) => s.ui.sidebarOpen);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        bgcolor: 'background.paper',
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
        transition: 'all 200ms ease',
      }}
    >
      <Toolbar sx={{ gap: 1 }}>
        <IconButton edge="start" onClick={() => dispatch(toggleSidebar())}>
          <MenuIcon />
        </IconButton>

        <ProjectSprintSwitcher />

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="outlined"
          color="inherit"
          startIcon={<SearchIcon />}
          onClick={() => dispatch(setGlobalSearch(true))}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            display: { xs: 'none', md: 'inline-flex' },
          }}
        >
          Search…
          <Box component="kbd" sx={{ ml: 2, opacity: 0.6, fontSize: 12 }}>
            Ctrl+K
          </Box>
        </Button>

        <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
          <IconButton onClick={toggle}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title={user?.email ?? ''}>
          <IconButton onClick={(e) => setAnchor(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32 }}>
              {(user?.displayName || user?.email || '?')[0].toUpperCase()}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">{user?.displayName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              setAnchor(null);
              logout();
            }}
          >
            <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
